// pages/blog/living-room-renovation-ideas.js

import Image from 'next/image';
import Head from 'next/head';
import Header from '../../components/Header';
import Hero from '../../components/Hero';
import SocialProof from '../../components/SocialProof';
import ContactUs from '../../components/ContactUs';
import Footer from '../../components/Footer';
import { generateArticleSchema } from '../../utils/schemaGenerators';

export const metadata = {
  title: '7 Living Room Renovation Ideas That Make a Big Impact',
  image: '/blog/living-room-renovation.webp',
  date: 'July 6, 2025',
  author: 'Modern Build Team',
  excerpt:
    'Transform your living room with these 7 renovation ideas that combine design, comfort, and smart upgrades—no full remodel needed.',
  category: 'Living Room Remodeling',
  tags: ['Living Room Remodeling', 'Home Remodeling', 'Interior Design'],
};

export default function LivingRoomRenovationIdeas() {
  return (
    <>
      <Head>
        <title>{metadata.title}</title>
        <meta name="title" content={metadata.title} />
        <meta name="description" content={metadata.excerpt} />
        <meta name="author" content={metadata.author} />
        <meta name="keywords" content={metadata.tags.join(', ')} />
        <link rel="canonical" href={`https://modernbuildremodel.com/blog/living-room-renovation-ideas`} />

        <meta property="og:title" content={metadata.title} />
        <meta property="og:description" content={metadata.excerpt} />
        <meta property="og:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta property="og:url" content={`https://modernbuildremodel.com/blog/living-room-renovation-ideas`} />
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Modern Build Home Remodeling" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.title} />
        <meta name="twitter:description" content={metadata.excerpt} />
        <meta name="twitter:image" content={`https://modernbuildremodel.com${metadata.image}`} />
        <meta name="twitter:site" content="@modernbuildhome" />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": "https://modernbuildremodel.com"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": "Blog",
                  "item": `https://modernbuildremodel.com/blog`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": "Living Room Renovation Ideas",
                  "item": `https://modernbuildremodel.com/blog/living-room-renovation-ideas`
                },
              ]
            })
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(generateArticleSchema(metadata, 'living-room-renovation-ideas')),
          }}
        />
      </Head>

      <Header />

      <div className="bg-white">
        <Hero category="livingRoomRenovationIdeas" page="page" hasContentH1={false}/>

        <div className="container mx-auto px-4 py-10 max-w-4xl">
          <p className="text-sm text-black mb-2">
            <span className="capitalize text-primary tracking-[3px]">By {metadata.author} / {metadata.date}</span>
          </p>

          <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] mb-10 rounded-lg overflow-hidden">
            <Image
              src={metadata.image}
              alt={`Modern Build Home Remodeling post: ${metadata.title}`}
              title={metadata.title}
              fill
              sizes="(max-width: 768px) 100vw, 700px"
              className="object-cover rounded-lg"
            />
          </div>

        <div className="prose prose-lg max-w-none text-md text-black leading-relaxed space-y-6">
          <p><strong>Your living room is the heart of your home</strong>—a place to relax, entertain, and create lasting memories. But if it feels outdated or uninspiring, it may be time for a refresh.</p>

          <p>You don’t always need a full remodel to make a big impact. Sometimes, just a few targeted upgrades can completely transform your space without blowing the budget.</p>

          <h3 className="font-bold">🪟 1. Open Up the Space</h3>
          <p>Removing a non-load-bearing wall or widening doorways can immediately make your living room feel more <strong>open, connected, and modern</strong>. It also improves traffic flow and natural lighting.</p>

          <h3 className="font-bold">🪵 2. Upgrade the Flooring</h3>
          <p>Say goodbye to worn-out carpet and hello to sleek finishes. Consider:</p>
          <ul className="list-disc pl-6">
            <li>Engineered hardwood for elegance</li>
            <li>Luxury vinyl plank (LVP) for durability</li>
            <li>Polished concrete for a minimalist vibe</li>
          </ul>
          <p>A new floor sets the tone for the entire space.</p>

          <h3 className="font-bold">🎨 3. Add an Accent Wall</h3>
          <p>Give your living room a focal point with a bold accent wall. Try:</p>
          <ul className="list-disc pl-6">
            <li>Paint in a dramatic or trendy color</li>
            <li>Textured wallpaper for depth</li>
            <li>Shiplap or wood slats for texture</li>
            <li>Stone veneer or brick for rustic charm</li>
          </ul>

          <h3 className="font-bold">💡 4. Modernize the Lighting</h3>
          <p>Outdated fixtures drag down the mood. Replace them with:</p>
          <ul className="list-disc pl-6">
            <li>Recessed lighting for general use</li>
            <li>Pendants for over furniture or art</li>
            <li>Sconces for layered ambiance</li>
          </ul>
          <p>Install dimmers to fine-tune the vibe for any time of day.</p>

          <h3 className="font-bold">🌞 5. Maximize Natural Light</h3>
          <p>Natural light transforms a room. Enhance it by:</p>
          <ul className="list-disc pl-6">
            <li>Swapping heavy drapes for sheer curtains</li>
            <li>Installing larger or additional windows</li>
            <li>Using mirrors to reflect light</li>
          </ul>

          <h3 className="font-bold">🛋️ 6. Reimagine Your Layout</h3>
          <p>Don’t just move furniture—<strong>rethink how you use the room</strong>. Ask yourself:</p>
          <ul className="list-disc pl-6">
            <li>Can you add a cozy reading nook?</li>
            <li>Would floating your sofa improve flow?</li>
            <li>Could the TV move to free up a focal wall?</li>
          </ul>
          <p>A smart layout makes the space feel fresh and functional.</p>

          <h3 className="font-bold">🧱 7. Built-Ins for Storage and Style</h3>
          <p>Custom built-ins are a smart way to add both organization and polish. Consider:</p>
          <ul className="list-disc pl-6">
            <li>Framing a fireplace or TV</li>
            <li>Adding bookshelves with accent lighting</li>
            <li>Creating low cabinetry for hidden storage</li>
          </ul>

          <p className="mt-8"><strong>Whether you implement one change or all seven</strong>, these renovation ideas will breathe new life into your living room—making it feel more modern, more comfortable, and more you.</p>
        </div>

        </div>

        <SocialProof />
        <ContactUs />
        <Footer />
      </div>
    </>
  );
}
